import type { MouseEventHandler, ReactNode } from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { playerProfilePath } from '../utils/profilePaths';

export type PlayerProfileLinkVariant = 'text' | 'identity' | 'quiet' | 'card';

export interface PlayerProfileLinkProps extends Omit<BoxProps, 'as'> {
  slug?: string | null;
  children: ReactNode;
  /** Accessible name when the children are not descriptive (e.g. an avatar). */
  label?: string;
  /**
   * `text` — inline handle/name hover underline.
   * `identity` — flex row; name accent on hover, no underline on the avatar.
   * `quiet` — avatar/chip; no underline.
   * `card` — full-width roster row.
   */
  variant?: PlayerProfileLinkVariant;
  /** Keep parent click handlers (team cards, facepile overflow) from firing. */
  stopPropagation?: boolean;
}

const VARIANT_STYLES: Record<PlayerProfileLinkVariant, BoxProps> = {
  text: {
    display: 'inline',
    _hover: { color: 'accent', textDecoration: 'underline' },
  },
  identity: {
    display: 'flex',
    alignItems: 'center',
    minW: 0,
    flex: 1,
    _hover: {
      textDecoration: 'none',
      '& [data-player-identity-name]': { color: 'accent' },
    },
  },
  quiet: {
    display: 'inline-flex',
    lineHeight: 0,
    _hover: { textDecoration: 'none', opacity: 0.88 },
  },
  card: {
    display: 'block',
    w: 'full',
    _hover: {
      textDecoration: 'none',
      '& [data-player-identity-card]': { borderColor: 'accent' },
    },
  },
};

/**
 * Link a player identity to `/players/:slug`.
 * Renders children unchanged when the slug is missing so callers can stay unconditional.
 */
export function PlayerProfileLink({
  slug,
  children,
  label,
  variant = 'text',
  stopPropagation = true,
  onClick,
  ...rest
}: PlayerProfileLinkProps) {
  const to = playerProfilePath(slug);
  if (!to) return <>{children}</>;

  const handleClick: MouseEventHandler<HTMLDivElement> = (event) => {
    if (stopPropagation) event.stopPropagation();
    onClick?.(event);
  };

  return (
    <Box
      as={RouterLink}
      {...({ to } as Record<string, unknown>)}
      aria-label={label}
      color="inherit"
      textDecoration="none"
      cursor="pointer"
      onClick={handleClick}
      {...VARIANT_STYLES[variant]}
      {...rest}
    >
      {children}
    </Box>
  );
}
